import {GridColDef} from '@mui/x-data-grid';
import {getAdvancedStatements, DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS} from 'Agent/Commissions/ducks/a360Commissions';
import React from 'react';
import {A360CommissionsGrid} from './CommissionsGrid';
import {A360AdvancedStatement} from '../types';
import {useDatagridState} from './useDatagridState';
import {A360CommissionSection} from '../CommissionSection';
import {currencyValueFormatter, extractProducerCodes, revertNoEmployerToEmpty} from '../utilities';

const advancedStatementColumns: GridColDef[] = [
  {field: 'employerId', headerName: 'Employer #', width: 150},
  {field: 'employerName', headerName: 'Employer Name', width: 200},
  {field: 'masterPolicyId', headerName: 'Master Policy #', width: 180},
  {field: 'productName', headerName: 'Product Name', width: 150},
  {field: 'productCode', headerName: 'Product Code', hide: true},
  {field: 'billingAccountId', headerName: 'Billing Account #', hide: true, width: 150},
  {field: 'billingAccountType', headerName: 'Billing Account Type', hide: true, width: 150},
  {field: 'certificateId', headerName: 'Certificate #', width: 150},
  {field: 'insuredName', headerName: 'Insured Name', width: 200},
  {field: 'certificateEffectiveDate', headerName: 'Certificate Effective Date', hide: true, width: 200},
  {
    field: 'annualizedPremium',
    headerName: 'Annualized Premium',
    width: 180,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'premiumSplitPercentage', headerName: 'Premium Split', hide: true, width: 150},
  {field: 'commissionType', headerName: 'Commission Type', hide: true, width: 150},
  {field: 'commissionRate', headerName: 'Commission Rate', hide: true, width: 150},
  {
    field: 'annualizedCommission',
    headerName: 'Annualized Commission',
    width: 200,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'advancePercentage', headerName: 'Advance Percentage', hide: true, width: 180},
  {
    field: 'commissionCalculated',
    headerName: 'Advance Commission Calculated',
    width: 250,
    valueFormatter: currencyValueFormatter,
  },
  {
    field: 'netAdvanceCommission',
    headerName: 'Net Advance Commission Payable',
    width: 250,
    valueFormatter: currencyValueFormatter,
  },
  {field: 'productionType', headerName: 'Production Type', hide: true, width: 150},
  {field: 'agencyCode', headerName: 'Producer Code', hide: true, width: 150},
  {field: 'producerName', headerName: 'Producer Name', hide: true, width: 150},
  {field: 'statementDate', headerName: 'Commission Statement Date', hide: true, width: 200},
  {field: 'reason', headerName: 'Reason', hide: true},
  {field: 'commissionStyle', headerName: 'Commission Style', hide: true, width: 150},
  {field: 'premiumSequenceEffectiveDate', headerName: 'Premium Sequence Effective Date', hide: true, width: 200},
].map(col => ({
  ...col,
  filterable: false,
  headerClassName: 'a360-commissions-grid__header',
}));

const getAdvancedStatementsRowId = (row: A360AdvancedStatement) =>
  row?.commissionStatementAdvancedId ??
  `${row.annualizedPremium}-${row.annualizedCommission}-${row.billingAccountId}-${row.billingAccountType}-${row.certificateId}-${row.certificateEffectiveDate}-${row.commissionCalculated}-${row.commissionRate}-${row.commissionType}-${row.employerId}-${row.employerName}-${row.insuredName}-${row.masterPolicyId}-${row.netAdvanceCommission}-${row.premiumSplitPercentage}-${row.productionType}-${row.producerName}-${row.agencyCode}-${row.productCode}-${row.productName}-${row.reason}-${row.statementDate}-${row.commissionStyle}-${row.premiumSequenceEffectiveDate}`;

export const AdvancedStatements = () => {
  const {filter, error, rows, loading, totalCount, handleFetch} = useDatagridState<A360AdvancedStatement>(
    'advancedStatements',
    getAdvancedStatements,
    DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS
  );

  return (
    <A360CommissionSection className="a360-commissions__advanced-statements" aria-label="advanced statements">
      <A360CommissionsGrid<A360AdvancedStatement>
        columns={advancedStatementColumns}
        error={error}
        getRowId={getAdvancedStatementsRowId}
        rows={rows}
        loading={loading}
        totalRecords={totalCount}
        onFetch={handleFetch}
        initialSort={[{field: DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS, sort: 'desc'}]}
        title="Advanced"
        toolbarProps={{
          exportToCsv: {
            requestPayload: {
              agencyCodes: extractProducerCodes(filter.selectedProducerCodes),
              statementDates: filter.selectedStatementDates,
              employerNames: revertNoEmployerToEmpty(filter.selectedEmployerNames),
              sortFields: [DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS],
            },
            downloadedFileName: 'AdvancedStatements',
            relativeFileLocation: '/api/commissions/v1/commissionStatements/advancedReport/csv',
          },
        }}
      />
    </A360CommissionSection>
  );
};
